// =============================================================================
// VandieZ shipper-web -- My Loads page (/)
// =============================================================================
// Lists the signed-in shipper's loads with status pills + a "Post a load" CTA.
// MVP: no per-load detail page yet — the row shows everything a shipper
// typically wants at a glance (pickup → drop, status, bid window, budget).
// =============================================================================

import { ChevronRight, Copy, Download, Package, Plus } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { loadsApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { Load, LoadStatus } from "@/types/load";

const STATUS_LABEL: Record<LoadStatus, string> = {
  draft: "Draft",
  open_for_bidding: "Open for bidding",
  accepted: "Accepted",
  completed: "Completed",
  cancelled: "Cancelled",
  expired: "Expired",
  postponed: "Postponed",
};

const STATUS_TONE: Record<LoadStatus, string> = {
  draft: "bg-muted text-muted-foreground",
  open_for_bidding: "bg-success-subtle text-success-subtle-foreground",
  accepted: "bg-primary-subtle text-primary-subtle-foreground",
  completed: "bg-muted text-muted-foreground",
  cancelled: "bg-danger-subtle text-danger-subtle-foreground",
  expired: "bg-muted text-muted-foreground",
  postponed: "bg-warning-subtle text-warning-subtle-foreground",
};

function formatINR(amount: string): string {
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) {return amount;}
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function MyLoadsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [loads, setLoads] = useState<Load[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // "Repost" navigates to /loads/new with the source load attached in
  // router state. PostLoadPage detects this and pre-fills the form so
  // the shipper can edit + submit without re-typing everything. No new
  // backend endpoint needed — it's just form pre-fill.
  function repost(load: Load): void {
    navigate("/loads/new", { state: { repost: load } });
  }

  useEffect(() => {
    if (accessToken === null) {return;}
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    loadsApi
      .list(accessToken, { limit: 50, offset: 0 }, controller.signal)
      .then((res) => {
        setLoads(res.items);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        setError(
          err instanceof ApiError
            ? err.problem.detail
            : "Couldn't load your loads. Try refreshing.",
        );
        setIsLoading(false);
      });
    return () => { controller.abort(); };
  }, [accessToken]);

  function exportCsv(): void {
    if (loads === null || loads.length === 0) {return;}
    const header = [
      "id",
      "status",
      "pickup_address",
      "pickup_district",
      "drop_address",
      "drop_district",
      "material_category",
      "weight_kg",
      "required_vehicle_type",
      "budget_min",
      "budget_max",
      "bid_window_ends_at",
      "created_at",
    ];
    const escape = (v: string): string =>
      /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    const csv = [
      header.join(","),
      ...loads.map((l) =>
        [
          l.id,
          l.status,
          l.pickup_address,
          l.pickup_district,
          l.drop_address,
          l.drop_district,
          l.material_category,
          l.weight_kg,
          l.required_vehicle_type,
          l.budget_min,
          l.budget_max,
          l.bid_window_ends_at,
          l.created_at,
        ]
          .map((c) => escape(String(c)))
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vandiez-my-loads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="My loads"
        description={
          loads === null
            ? "Loading…"
            : `${loads.length} load${loads.length === 1 ? "" : "s"} posted.`
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={loads === null || loads.length === 0}
            >
              <Download className="mr-1 h-4 w-4" />
              Export CSV
            </Button>
            <Link to="/loads/new">
              <Button>
                <Plus className="h-4 w-4" />
                Post a load
              </Button>
            </Link>
          </div>
        }
      />

        {error !== null ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base text-danger-subtle-foreground">
                Couldn't load your loads
              </CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
          </Card>
        ) : null}

        {!isLoading && loads !== null && loads.length === 0 ? (
          <Card>
            <CardHeader className="items-center text-center">
              <Package className="h-10 w-10 text-muted-foreground" />
              <CardTitle>No loads yet</CardTitle>
              <CardDescription>
                Post your first load and drivers in your area will start
                bidding within minutes.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Link to="/loads/new">
                <Button>
                  <Plus className="h-4 w-4" />
                  Post a load
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : null}

        <div className="space-y-3">
          {loads?.map((load) => (
            <Link
              key={load.id}
              to={`/loads/${load.id}`}
              className="block transition-colors hover:opacity-95"
            >
            <Card className="group cursor-pointer transition-colors hover:border-primary/50">
              <CardContent className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[load.status]}`}
                    >
                      {STATUS_LABEL[load.status]}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {load.material_category} · {load.weight_kg} kg ·{" "}
                      {load.required_vehicle_type}
                    </span>
                  </div>
                  <p className="text-sm font-medium leading-snug">
                    {load.pickup_district} → {load.drop_district}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Pickup {formatDate(load.pickup_window_start)} · Posted{" "}
                    {formatDate(load.created_at)}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-3 md:justify-end md:text-right md:min-w-[10rem]">
                  <div className="md:text-right">
                  <p className="text-sm font-mono tabular-nums">
                    {formatINR(load.budget_min)}–{formatINR(load.budget_max)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {load.estimated_distance_km} km
                  </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        repost(load);
                      }}
                      title="Use this load as a template for a new posting"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                  </div>
                </div>
              </CardContent>
            </Card>
            </Link>
          ))}
        </div>
    </div>
  );
}
